import { Button, Col, Row, Space } from "antd";
import { ICalendarEvent, IInteractiveModCalendar } from "@blings/blings-player";
import { useEffect, useState } from "react";
import { useMst } from "../../../../stores/main";
import { JsEditor } from "../../../Editor/JsEditor";
import { ExpressionIcon } from "../../../../icons-and-animations/Icons";
import {
  getActiveCalendarSource,
  getCalendarExpressionFromEvent,
  getCalendarSourceUpdate,
  parseCalendarEventValue,
} from "../../calendarConnector";
import { CalendarEventEditor } from "./CalendarEventEditor";
import {
  getCalendarSourceDraft,
  saveCalendarSourceDraft,
} from "./calendarSourceDrafts";

const CalendarSourceEditorContent = ({
  change,
  index,
  onChange,
  draftKey,
}: {
  change: IInteractiveModCalendar;
  index: number;
  onChange: (index: number, keys: any, values: any) => void;
  draftKey: string;
}) => {
  const storedDraft = getCalendarSourceDraft(draftKey, change);
  const [useCode, setUseCode] = useState(
    () => getActiveCalendarSource(change) === "expression"
  );
  const [formDraft, setFormDraft] = useState<ICalendarEvent>(storedDraft.form);
  const [codeDraft, setCodeDraft] = useState(storedDraft.code);

  useEffect(() => {
    const event = parseCalendarEventValue(change.value);
    if (event) {
      setFormDraft(event);
      saveCalendarSourceDraft(draftKey, { form: event });
    }
  }, [change.value, draftKey]);

  useEffect(() => {
    if (typeof change.expression === "string") {
      setCodeDraft(change.expression);
      saveCalendarSourceDraft(draftKey, { code: change.expression });
    }
  }, [change.expression, draftKey]);

  useEffect(() => {
    if (typeof change.value !== "undefined") {
      setUseCode(false);
    } else if (typeof change.expression !== "undefined") {
      setUseCode(true);
    }
  }, [change.expression, change.value]);

  const updateSource = (
    source: "value" | "expression",
    sourceValue: ICalendarEvent | string
  ) => {
    const update = getCalendarSourceUpdate(source, sourceValue);
    onChange(index, update.keys, update.values);
  };

  const openCode = () => {
    const nextCode = getCalendarExpressionFromEvent(formDraft, codeDraft);
    setUseCode(true);
    setCodeDraft(nextCode);
    saveCalendarSourceDraft(draftKey, { form: formDraft, code: nextCode });
    updateSource("expression", nextCode);
  };

  const openForm = () => {
    setUseCode(false);
    saveCalendarSourceDraft(draftKey, { form: formDraft, code: codeDraft });
    updateSource("value", formDraft);
  };

  return (
    <Space direction="vertical" className="calendar-source-editor" size={12}>
      {useCode ? (
        <Col className="calendar-code-source">
          <Row justify="end">
            <Button
              aria-label="Use calendar form"
              title="Use calendar form"
              className="active-code-icon"
              style={{
                display: "flex",
                alignItems: "center",
              }}
              onClick={openForm}
            >
              <ExpressionIcon />
            </Button>
          </Row>
          <JsEditor
            defaultValue={change.expression || codeDraft}
            onChange={(expression) => {
              setCodeDraft(expression);
              saveCalendarSourceDraft(draftKey, { code: expression });
              updateSource("expression", expression);
            }}
            style={{ marginTop: 0 }}
            tooltipText="Return an event object. You can use the data and control objects."
          />
        </Col>
      ) : (
        <CalendarEventEditor
          value={parseCalendarEventValue(change.value) || formDraft}
          onChange={(event) => {
            setFormDraft(event);
            saveCalendarSourceDraft(draftKey, { form: event });
            updateSource("value", event);
          }}
          onOpenCode={openCode}
        />
      )}
    </Space>
  );
};

export const CalendarSourceEditor = (props: {
  change: IInteractiveModCalendar;
  index: number;
  onChange: (index: number, keys: any, values: any) => void;
}) => {
  const {
    platformStore: { selectedProjectId, selectedVideoPartName },
  } = useMst();
  const draftKey = `${selectedProjectId || "project"}:${
    selectedVideoPartName || "scene"
  }:${props.index}`;

  return (
    <CalendarSourceEditorContent
      key={draftKey}
      {...props}
      draftKey={draftKey}
    />
  );
};
